<template>
  <section class="section">
    <nav class="level" style="margin: 20px; align-items: end;">
      <div class="level-left">
        <div class="level-item" style="flex-direction: column; align-items: flex-start;">
          <h1 class="title is-5">Precios de mercado</h1>

          <div class="filter-bar">
            <input
              class="input"
              type="text"
              placeholder="Buscar modelo..."
              v-model="filterModel"
              style="width: 220px;"
            />

            <div class="storage-chips">
              <button
                v-for="s in availableStorages"
                :key="s"
                type="button"
                class="storage-chip"
                :class="{ 'is-active': filterStorages.includes(s) }"
                @click="toggleStorage(s)"
              >
                {{ s }}
              </button>
            </div>

            <div class="select" v-if="availableBrands.length">
              <select v-model="filterBrand">
                <option value="">Todas las marcas</option>
                <option v-for="b in availableBrands" :key="b" :value="b">{{ b }}</option>
              </select>
            </div>

            <div class="select">
              <select v-model="filterCondition">
                <option value="">Toda condición</option>
                <option value="nuevo">Nuevo</option>
                <option value="reacondicionado">Reacondicionado</option>
              </select>
            </div>

            <button
              v-if="hasActiveFilters"
              class="button is-small is-light"
              @click="clearFilters"
            >
              Limpiar
            </button>
          </div>

          <p class="results-count">
            Mostrando <strong>{{ filteredMarketPrices.length }}</strong>
            de {{ marketPrices.length }} productos
          </p>
        </div>
      </div>

      <div class="level-right">
        <div class="button-grid">
          <button
            v-if="showEditColumn"
            class="button is-warning is-light"
            :class="{ 'is-loading': isConfigLoading }"
            @click="openConfigModal"
            :disabled="isLoading || isConfigLoading"
          >
            Ajustes de Algoritmo
          </button>

          <button
            class="button is-primary"
            @click="downloadMarketPricesCsv"
            :disabled="isLoading"
          >
            Descargar CSV
          </button>

          <button
            v-if="showEditColumn"
            class="button is-link is-light"
            @click="downloadEditTemplate"
            :disabled="isLoading"
          >
            Plantilla edición masiva
          </button>

          <button
            v-if="showEditColumn"
            class="button is-success is-light"
            @click="triggerBulkUpload"
            :disabled="isLoading"
          >
            Cargar precios (CSV)
          </button>

          <input
            ref="bulkFileInput"
            type="file"
            accept=".csv"
            style="display: none;"
            @change="onBulkFileSelected"
          />
        </div>
      </div>
    </nav>

    <div class="box" style="margin-top: 20px;">
      <base-table
        class="is-bordered is-striped is-narrow is-fullwidth is-hoverable"
        :columns="computedColumnsData"
        :data="filteredMarketPrices"
        :loading="isLoading"
      >
        <template v-slot:brand="{ row }">
          <span v-if="row.brand" class="brand-text">{{ row.brand }}</span>
          <span v-else class="has-text-grey">—</span>
        </template>

        <template v-slot:condicion="{ row }">
          <span v-if="row.discountPct == null" class="has-text-grey">—</span>
          <span
            v-else
            class="cond-pill"
            :class="row.reconditioned ? 'cond-reac' : 'cond-nuevo'"
          >
            {{ row.reconditioned ? 'Reacondicionado' : 'Nuevo' }} &minus;{{ row.discountPct }}%
          </span>
        </template>

        <template v-slot:originalPrice="{ row }">
          <span class="price-original">${{ formatMoney(row.originalPrice) }}</span>
        </template>

        <template v-slot:gradeA="{ row }">
          <span class="grade-pill grade-a">${{ formatMoney(row.A) }}</span>
        </template>

        <template v-slot:gradeAB="{ row }">
          <span class="grade-pill grade-ab">${{ formatMoney(row.AB) }}</span>
        </template>

        <template v-slot:gradeB="{ row }">
          <span class="grade-pill grade-b">${{ formatMoney(row.B) }}</span>
        </template>

        <template v-slot:gradeC="{ row }">
          <span class="grade-pill grade-c">${{ formatMoney(row.C) }}</span>
        </template>

        <template v-slot:consignment="{ row }">
          <span v-if="row.consignment != null" class="grade-pill grade-consignment">${{ formatMoney(row.consignment) }}</span>
          <span v-else class="has-text-grey">—</span>
        </template>

        <template v-slot:actions="{ row }">
          <button class="button is-small is-info" @click="openEditModal(row)">
            Editar
          </button>
        </template>
      </base-table>
    </div>

    <!-- Modal Editar Precio Individual -->
    <div class="modal" :class="{ 'is-active': isEditModalOpen }">
      <div class="modal-background" @click="closeEditModal"></div>

      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            Editar precios – {{ selectedProduct?.model }} {{ selectedProduct?.storage }}
          </p>
          <button class="delete" @click="closeEditModal"></button>
        </header>

        <section class="modal-card-body">
          <div class="field">
            <label class="label">Mediana</label>
            <div class="control has-icons-left">
              <input type="number" class="input" v-model.number="editForm.median" />
              <span class="icon is-left" style="color: #666; font-weight: bold;">$</span>
            </div>
            <p class="help">Los grados se recalculan automáticamente desde la mediana.</p>
          </div>

          <div class="columns is-multiline">
            <div class="column is-half" v-for="grade in ['A','AB','B','C']" :key="grade">
              <div class="field">
                <label class="label">Grado {{ grade }}</label>
                <div style="display: flex; gap: 0.5rem; align-items: center;">
                  <div style="flex: 1; text-align: center; padding: 0.5em; background: #f5f5f5; border-radius: 4px; font-weight: 600;">
                    {{ formatMoney(originalPrices[grade]) }}
                  </div>

                  <div style="padding: 0 0.25rem; font-weight: bold; color: #555;">
                    →
                  </div>

                  <div style="flex: 1; text-align: center; padding: 0.5em; background: #eaf7f3; border-radius: 4px; font-weight: 700; color: #00947e;">
                    {{ formatMoney(editPreviewGrades[grade]) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer class="modal-card-foot">
          <button class="button is-success" @click="savePrices" :disabled="isLoading">
            Guardar
          </button>
          <button class="button" @click="closeEditModal">
            Cancelar
          </button>
        </footer>
      </div>
    </div>

    <!-- Modal Configuración del Algoritmo -->
    <div class="modal" :class="{ 'is-active': isConfigModalOpen }">
      <div class="modal-background" @click="closeConfigModal"></div>

      <div class="modal-card" style="width: 750px;">
        <header class="modal-card-head">
          <p class="modal-card-title">Ajustes de Algoritmo</p>
          <button class="delete" @click="closeConfigModal" :disabled="isSavingConfig"></button>
        </header>

        <section class="modal-card-body">
          <article class="message is-warning is-small">
            <div class="message-body">
              <strong>Atención:</strong> Al guardar estos cambios, se recalculará automáticamente el precio de mercado de <strong>miles de productos</strong> afectando todo Givu.
            </div>
          </article>

          <article class="message is-info is-small">
            <div class="message-body">
              <strong>Fórmula Base Aplicada:</strong><br>
              <span style="font-family: monospace;">Precio sugerido grado AB = ( (Precio Mediana * Factor Retención) - Gastos Fijos ) * Margen de Utilidad</span><br>
            </div>
          </article>

          <div class="columns" style="margin-top: 5px;">
            <div class="column">
              <div class="field">
                <label class="label">Factor de Retención</label>
                <div class="control has-icons-right">
                  <input class="input has-text-centered" type="number" step="1" v-model.number="displayFactorRetencion" />
                  <span class="icon is-right" style="color: #666; font-weight: bold;">%</span>
                </div>
              </div>
            </div>

            <div class="column">
              <div class="field">
                <label class="label">Gastos Fijos</label>
                <div class="control has-icons-left">
                  <input class="input has-text-centered" type="number" step="10" v-model.number="displayGastosFijos" />
                  <span class="icon is-left" style="color: #666; font-weight: bold;">$</span>
                </div>
              </div>
            </div>

            <div class="column">
              <div class="field">
                <label class="label">Margen de Utilidad (AB)</label>
                <div class="control has-icons-right">
                  <input class="input has-text-centered" type="number" step="1" v-model.number="displayMargenUtilidad" />
                  <span class="icon is-right" style="color: #666; font-weight: bold;">%</span>
                </div>
              </div>
            </div>
          </div>

          <p class="is-size-7 has-text-grey" style="margin: 6px 2px 0;">
            Margen de utilidad de los demás grados (se aplican sobre la misma base que AB):
          </p>
          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label">Margen Grado A</label>
                <div class="control has-icons-right">
                  <input class="input has-text-centered" type="number" step="1" v-model.number="displayMargenA" />
                  <span class="icon is-right" style="color: #666; font-weight: bold;">%</span>
                </div>
              </div>
            </div>

            <div class="column">
              <div class="field">
                <label class="label">Margen Grado B</label>
                <div class="control has-icons-right">
                  <input class="input has-text-centered" type="number" step="1" v-model.number="displayMargenB" />
                  <span class="icon is-right" style="color: #666; font-weight: bold;">%</span>
                </div>
              </div>
            </div>

            <div class="column">
              <div class="field">
                <label class="label">Margen Grado C</label>
                <div class="control has-icons-right">
                  <input class="input has-text-centered" type="number" step="1" v-model.number="displayMargenC" />
                  <span class="icon is-right" style="color: #666; font-weight: bold;">%</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="specialPartners.length" style="margin-top: 10px;">
            <p class="is-size-7 has-text-grey" style="margin: 0 2px 4px;">
              Excepciones por convenio — margen de AB propio (solo lectura):
            </p>
            <div class="columns is-multiline">
              <div class="column is-one-third" v-for="p in specialPartners" :key="p.code">
                <div class="field">
                  <label class="label">{{ p.name }}</label>
                  <div class="control has-icons-right">
                    <input class="input has-text-centered" type="number" :value="Math.round((p.abGrade || 0) * 100)" disabled />
                    <span class="icon is-right" style="color: #666; font-weight: bold;">%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style="background: #fcfcfc; padding: 20px; border-radius: 6px; border: 1px dashed #c4c4c4; margin-top: 10px;">
            <p class="title is-6" style="margin-bottom: 5px; color: #4a4a4a;">Ejemplo de Simulación de un Producto</p>
            <p class="is-size-7 has-text-grey" style="margin-bottom: 12px;">Se asume un celular con un precio de Mediana de <strong>$10,000 MXN</strong>.</p>

            <div style="font-family: monospace; font-size: 14px; line-height: 1.8; color: #363636;">
              <span style="opacity: 0.7;">paso 1:</span> ( $10,000 * <strong>{{(displayFactorRetencion || 0) / 100}}</strong> ) = ${{ formatMoney(10000 * ((displayFactorRetencion || 0) / 100)) }} <br>
              <span style="opacity: 0.7;">paso 2:</span> ${{ formatMoney(10000 * ((displayFactorRetencion || 0) / 100)) }} - <strong>${{displayGastosFijos || 0}}</strong> = ${{ formatMoney((10000 * ((displayFactorRetencion || 0) / 100)) - (displayGastosFijos || 0)) }} <br>
              <span style="opacity: 0.7;">paso 3:</span> ${{ formatMoney((10000 * ((displayFactorRetencion || 0) / 100)) - (displayGastosFijos || 0)) }} * <strong>{{(displayMargenUtilidad || 0) / 100}}</strong>
              <br><br>
              <strong style="font-size: 16px; color: #00d1b2;">=&gt; Precio Sugerido AB = ${{ formatMoney(ejemploGivuPrice) }}</strong><br>
              <div style="margin-top: 12px;">
                <span class="grade-pill grade-a">A ${{ formatMoney(ejemploGrados.A) }}</span>
                <span class="grade-pill grade-ab">AB ${{ formatMoney(ejemploGrados.AB) }}</span>
                <span class="grade-pill grade-b">B ${{ formatMoney(ejemploGrados.B) }}</span>
                <span class="grade-pill grade-c">C ${{ formatMoney(ejemploGrados.C) }}</span>
              </div>
            </div>
          </div>

        </section>

        <footer class="modal-card-foot">
          <button class="button is-success" :class="{ 'is-loading': isSavingConfig }" @click="savePricingParams" :disabled="isSavingConfig">
            Guardar y Ejecutar Recálculo
          </button>
          <button class="button" @click="closeConfigModal" :disabled="isSavingConfig">
            Cancelar
          </button>
        </footer>
      </div>
    </div>

    <!-- Modal Preview Carga Masiva -->
    <div class="modal" :class="{ 'is-active': isBulkModalOpen }">
      <div class="modal-background" @click="closeBulkModal"></div>

      <div class="modal-card" style="width: 960px; max-width: 95vw;">
        <header class="modal-card-head">
          <p class="modal-card-title">
            Revisar precios a actualizar
            <span v-if="bulkFileName" class="is-size-7 has-text-grey" style="font-weight: 400;">
              — {{ bulkFileName }}
            </span>
          </p>
          <button class="delete" @click="closeBulkModal" :disabled="isApplyingBulk"></button>
        </header>

        <section class="modal-card-body">
          <div v-if="bulkInvalidCount || bulkNotFoundCount" class="notification is-warning is-light is-small">
            <span v-if="bulkNotFoundCount">{{ bulkNotFoundCount }} fila(s) con id no encontrado se ignoraron. </span>
            <span v-if="bulkInvalidCount">{{ bulkInvalidCount }} fila(s) con precio inválido se ignoraron.</span>
          </div>

          <article class="message is-info is-small">
            <div class="message-body">
              <strong>{{ bulkChangedCount }}</strong> producto(s) se modificarán.
              Las filas resaltadas son las que cambian; revisá y confirmá.
            </div>
          </article>

          <div v-if="bulkRows.length" class="table-container" style="max-height: 50vh; overflow-y: auto;">
            <table class="table is-bordered is-striped is-narrow is-fullwidth is-hoverable">
              <thead>
                <tr>
                  <th>Modelo</th>
                  <th class="has-text-centered">Almac.</th>
                  <th class="has-text-centered">Mediana</th>
                  <th class="has-text-centered">A</th>
                  <th class="has-text-centered">AB</th>
                  <th class="has-text-centered">B</th>
                  <th class="has-text-centered">C</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in bulkRows" :key="row.id" :class="{ 'row-changed': row.changed }">
                  <td style="font-weight: 600;">{{ row.model }}</td>
                  <td class="has-text-centered">{{ row.storage }}</td>
                  <td class="has-text-centered">
                    <span class="old-val">{{ formatMoney(row.oldBase) }}</span>
                    <span class="arrow">→</span>
                    <span class="new-val">{{ formatMoney(row.newBase) }}</span>
                  </td>
                  <td class="has-text-centered">
                    <span class="old-val">{{ formatMoney(row.oldGrades.A) }}</span>
                    <span class="arrow">→</span>
                    <span class="new-val">{{ formatMoney(row.newGrades.A) }}</span>
                  </td>
                  <td class="has-text-centered">
                    <span class="old-val">{{ formatMoney(row.oldGrades.AB) }}</span>
                    <span class="arrow">→</span>
                    <span class="new-val">{{ formatMoney(row.newGrades.AB) }}</span>
                  </td>
                  <td class="has-text-centered">
                    <span class="old-val">{{ formatMoney(row.oldGrades.B) }}</span>
                    <span class="arrow">→</span>
                    <span class="new-val">{{ formatMoney(row.newGrades.B) }}</span>
                  </td>
                  <td class="has-text-centered">
                    <span class="old-val">{{ formatMoney(row.oldGrades.C) }}</span>
                    <span class="arrow">→</span>
                    <span class="new-val">{{ formatMoney(row.newGrades.C) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p v-else class="has-text-grey has-text-centered" style="padding: 2rem;">
            No se encontraron filas con un nuevo precio para aplicar.
          </p>
        </section>

        <footer class="modal-card-foot">
          <button
            class="button is-success"
            :class="{ 'is-loading': isApplyingBulk }"
            @click="applyBulkChanges"
            :disabled="isApplyingBulk || bulkChangedCount === 0"
          >
            Aplicar {{ bulkChangedCount }} cambio(s)
          </button>
          <button class="button" @click="closeBulkModal" :disabled="isApplyingBulk">
            Cancelar
          </button>
        </footer>
      </div>
    </div>

  </section>
</template>

<script>
import { mapActions } from 'vuex'

const ADMIN_PARRA_ID = 33
const ADMIN_MISH_ID = 43
const ADMIN_GERMAN_ID = 51

export default {
  name: 'MarketPrices',
  components: {
    BaseTable: () => import('@/components/Table')
  },
  data () {
    return {
      isLoading: false,
      isConfigLoading: false,
      isSavingConfig: false,
      marketPrices: [],
      filterModel: '',
      filterStorages: [],
      filterBrand: '',
      filterCondition: '',
      isEditModalOpen: false,
      isConfigModalOpen: false,
      isBulkModalOpen: false,
      isApplyingBulk: false,
      bulkFileName: '',
      bulkRows: [],
      bulkNotFoundCount: 0,
      bulkInvalidCount: 0,
      selectedProduct: null,
      editForm: { median: 0 },
      originalPrices: { A: 0, AB: 0, B: 0, C: 0 },
      grades: { A: 0.80, AB: 0.70, B: 0.50, C: 0.40 },
      specialPartners: [],
      displayFactorRetencion: 0,
      displayGastosFijos: 0,
      displayMargenUtilidad: 0,
      displayMargenA: 0,
      displayMargenB: 0,
      displayMargenC: 0,
      factorRetencion: 0.8,
      gastosFijos: 280,
      showEditColumn: false,
      columnsData: [
        { id: 'brand', name: 'brand', header: 'Marca', class: 'col-brand' },
        { id: 'model', header: 'Modelo', accessor: row => row.model, class: 'col-model' },
        { id: 'storage', header: 'Almacenamiento', accessor: row => row.storage, class: 'has-text-centered' },
        { id: 'condicion', name: 'condicion', header: 'Condición', class: 'has-text-centered' },
        { id: 'originalPrice', name: 'originalPrice', header: 'Mediana', class: 'has-text-right col-price' },
        { id: 'A', name: 'gradeA', header: 'Grado A', class: 'has-text-centered col-grade' },
        { id: 'AB', name: 'gradeAB', header: 'Grado AB', class: 'has-text-centered col-grade col-grade-ab' },
        { id: 'B', name: 'gradeB', header: 'Grado B', class: 'has-text-centered col-grade' },
        { id: 'C', name: 'gradeC', header: 'Grado C', class: 'has-text-centered col-grade' },
        {
          id: 'actions',
          name: 'actions',
          header: 'Acciones',
          class: 'has-text-centered',
          show: true
        }
      ]
    }
  },

  computed: {
    computedColumnsData () {
      let cols = this.columnsData.filter(col => {
        if (col.id === 'actions') return this.showEditColumn
        return true
      })

      if (this.specialPartners.length) {
        const partnerCols = this.specialPartners.map(p => ({
          id: `partner_${p.code}`,
          header: p.name,
          accessor: row => `$${this.formatMoney(row[`partner_${p.code}`])}`,
          class: 'has-text-centered col-grade col-grade-partner'
        }))

        const abIndex = cols.findIndex(c => c.id === 'AB')
        const insertAt = abIndex >= 0 ? abIndex + 1 : cols.length
        cols = [...cols.slice(0, insertAt), ...partnerCols, ...cols.slice(insertAt)]
      }

      if (this.hasConsignment) {
        const consignmentCol = {
          id: 'consignment',
          name: 'consignment',
          header: 'Consignación',
          class: 'has-text-centered col-grade col-grade-consignment'
        }
        const cIndex = cols.findIndex(c => c.id === 'C')
        const insertAt = cIndex >= 0 ? cIndex + 1 : cols.length
        cols = [...cols.slice(0, insertAt), consignmentCol, ...cols.slice(insertAt)]
      }

      return cols
    },

    hasConsignment () {
      return this.marketPrices.some(row => row.consignment != null)
    },

    availableStorages () {
      const set = new Set(
        this.marketPrices.map(it => it.storage).filter(Boolean)
      )
      return [...set].sort((a, b) => this.storageToGb(a) - this.storageToGb(b))
    },

    availableBrands () {
      const set = new Set(
        this.marketPrices.map(it => it.brand).filter(Boolean)
      )
      return [...set].sort((a, b) => a.localeCompare(b))
    },

    hasActiveFilters () {
      return !!(this.filterModel || this.filterStorages.length || this.filterBrand || this.filterCondition)
    },

    filteredMarketPrices () {
      const term = this.filterModel.trim().toLowerCase()

      return this.marketPrices.filter(item => {
        if (term && !(item.model || '').toLowerCase().includes(term)) return false
        if (this.filterStorages.length && !this.filterStorages.includes(item.storage)) return false
        if (this.filterBrand && item.brand !== this.filterBrand) return false
        if (this.filterCondition) {
          const isReac = item.reconditioned === true
          if (this.filterCondition === 'reacondicionado' && !isReac) return false
          if (this.filterCondition === 'nuevo' && item.reconditioned !== false) return false
        }
        return true
      })
    },

    ejemploGivuPrice () {
      const fr = (this.displayFactorRetencion || 0) / 100
      const gf = (this.displayGastosFijos || 0)
      const mu = (this.displayMargenUtilidad || 0) / 100
      const calc = ((10000 * fr) - gf) * mu
      return calc > 0 ? calc : 0
    },

    ejemploBase () {
      const fr = (this.displayFactorRetencion || 0) / 100
      const gf = (this.displayGastosFijos || 0)
      const base = (10000 * fr) - gf
      return base > 0 ? base : 0
    },

    ejemploGrados () {
      const base = this.ejemploBase
      return {
        A: Math.round(base * ((this.displayMargenA || 0) / 100)),
        AB: Math.round(base * ((this.displayMargenUtilidad || 0) / 100)),
        B: Math.round(base * ((this.displayMargenB || 0) / 100)),
        C: Math.round(base * ((this.displayMargenC || 0) / 100))
      }
    },

    ejemploPrecioBase () {
      return this.ejemploGivuPrice / this.grades.AB
    },

    bulkChangedCount () {
      return this.bulkRows.filter(r => r.changed).length
    },

    editPreviewGrades () {
      const base = this.marketBaseFront(this.editForm.median)
      return {
        A: Math.round(base * this.grades.A),
        AB: Math.round(base * this.grades.AB),
        B: Math.round(base * this.grades.B),
        C: Math.round(base * this.grades.C)
      }
    }
  },

  beforeMount () {
    this.showEditColumn = [ADMIN_PARRA_ID, ADMIN_MISH_ID, ADMIN_GERMAN_ID].includes(
      this.$store.state.authentication.adminId
    )
  },

  mounted () {
    this.loadGrades()
    this.getMarketPrices()
  },

  methods: {
    ...mapActions([
      'getMarketPricesDB',
      'updateMarketPriceDB',
      'csvDownloadMarketPricesDB',
      'csvEditTemplateMarketPricesDB',
      'bulkUpdateMarketPricesDB',
      'addTemporalNotification',
      'getPricingParamsDB',
      'updatePricingParamsDB'
    ]),

    downloadCsvString (csvString, prefix) {
      const today = new Date()
      const yyyy = today.getFullYear()
      const mm = String(today.getMonth() + 1).padStart(2, '0')
      const dd = String(today.getDate()).padStart(2, '0')
      const exportName = `${prefix}_${yyyy}-${mm}-${dd}.csv`

      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })

      if (navigator.msSaveBlob) {
        navigator.msSaveBlob(blob, exportName)
      } else {
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', exportName)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },

    formatMoney (value) {
      if (!value) return '0'
      return Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })
    },

    storageToGb (storage) {
      const raw = String(storage || '').toUpperCase()
      const match = raw.match(/([\d.]+)\s*(TB|GB)?/)
      if (!match) return Number.MAX_SAFE_INTEGER
      const num = parseFloat(match[1]) || 0
      return match[2] === 'TB' ? num * 1024 : num
    },

    toggleStorage (storage) {
      const idx = this.filterStorages.indexOf(storage)
      if (idx === -1) {
        this.filterStorages.push(storage)
      } else {
        this.filterStorages.splice(idx, 1)
      }
    },

    clearFilters () {
      this.filterModel = ''
      this.filterStorages = []
      this.filterBrand = ''
      this.filterCondition = ''
    },

    marketBaseFront (median) {
      const base = (Number(median) || 0) * this.factorRetencion - this.gastosFijos
      return base > 0 ? base : 0
    },

    async loadGrades () {
      try {
        const resp = await this.getPricingParamsDB()
        if (resp?.payload?.GRADES) {
          this.grades = resp.payload.GRADES
        }
        if (resp?.payload) {
          if (resp.payload.FACTOR_RETENCION != null) this.factorRetencion = Number(resp.payload.FACTOR_RETENCION)
          if (resp.payload.GASTOS_FIJOS != null) this.gastosFijos = Number(resp.payload.GASTOS_FIJOS)
        }
      } catch (e) {
        console.error('No se pudieron cargar los grados de precio', e)
      }
    },

    async getMarketPrices () {
      this.isLoading = true
      const resp = await this.getMarketPricesDB()
      if (resp?.payload) {
        const withPartners = resp.payload.find(it => Array.isArray(it.partnerPricing) && it.partnerPricing.length)
        this.specialPartners = withPartners
          ? withPartners.partnerPricing.map(p => ({ name: p.name, code: p.code, abGrade: p.abGrade }))
          : []

        this.marketPrices = resp.payload.map(item => {
          const discountPct = item.discount_pct != null ? Number(item.discount_pct) : null
          const row = {
            id: item.id,
            model: item.model,
            storage: item.storage,
            brand: item.brand || null,
            originalPrice: item.market_price,
            discountPct,
            reconditioned: item.reconditioned != null
              ? item.reconditioned
              : (discountPct != null ? discountPct === 10 : null),
            A: item.pricing?.A,
            AB: item.pricing?.AB,
            B: item.pricing?.B,
            C: item.pricing?.C,
            consignment: item.consignment != null ? item.consignment : null
          };
          (item.partnerPricing || []).forEach(p => {
            row[`partner_${p.code}`] = p.value
          })
          return row
        })
      }
      this.isLoading = false
    },

    async openConfigModal () {
      this.isConfigLoading = true
      const resp = await this.getPricingParamsDB()
      if (resp?.payload) {
        this.displayFactorRetencion = Math.round(Number(resp.payload.FACTOR_RETENCION) * 100)
        this.displayGastosFijos = Number(resp.payload.GASTOS_FIJOS)
        this.displayMargenUtilidad = Math.round(Number(resp.payload.MARGEN_UTILIDAD ?? resp.payload.MARGEN_AB ?? resp.payload.GRADES?.AB ?? 0.70) * 100)
        this.displayMargenA = Math.round(Number(resp.payload.MARGEN_A ?? resp.payload.GRADES?.A ?? 0.80) * 100)
        this.displayMargenB = Math.round(Number(resp.payload.MARGEN_B ?? resp.payload.GRADES?.B ?? 0.50) * 100)
        this.displayMargenC = Math.round(Number(resp.payload.MARGEN_C ?? resp.payload.GRADES?.C ?? 0.40) * 100)
      }
      this.isConfigLoading = false
      this.isConfigModalOpen = true
    },

    closeConfigModal () {
      this.isConfigModalOpen = false
    },

    async savePricingParams () {
      try {
        this.isSavingConfig = true

        const paramsToSave = {
          FACTOR_RETENCION: this.displayFactorRetencion / 100,
          GASTOS_FIJOS: this.displayGastosFijos,
          MARGEN_UTILIDAD: this.displayMargenUtilidad / 100,
          MARGEN_AB: this.displayMargenUtilidad / 100,
          MARGEN_A: this.displayMargenA / 100,
          MARGEN_B: this.displayMargenB / 100,
          MARGEN_C: this.displayMargenC / 100
        }

        const success = await this.updatePricingParamsDB(paramsToSave)
        if (success) {
          this.closeConfigModal()
          await this.loadGrades()
          await this.getMarketPrices()
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.isSavingConfig = false
      }
    },

    openEditModal (row) {
      this.selectedProduct = row
      this.editForm = { median: row.originalPrice }
      this.originalPrices = {
        A: row.A,
        AB: row.AB,
        B: row.B,
        C: row.C
      }
      this.isEditModalOpen = true
    },

    closeEditModal () {
      this.isEditModalOpen = false
      this.selectedProduct = null
    },

    async savePrices () {
      try {
        this.isLoading = true
        await this.updateMarketPriceDB({
          id: this.selectedProduct.id,
          median: this.editForm.median
        })
        await this.getMarketPrices()
        this.closeEditModal()
      } catch (e) {
        this.addTemporalNotification({ type: 'danger', message: 'Error al actualizar precio' })
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },

    async downloadMarketPricesCsv () {
      this.isLoading = true
      try {
        const resp = await this.csvDownloadMarketPricesDB()
        if (!resp) throw new Error('No data')
        this.downloadCsvString(resp, 'MarketPrices')
      } catch (e) {
        this.addTemporalNotification({
          type: 'danger',
          message: 'Error en la descarga del CSV'
        })
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },

    async downloadEditTemplate () {
      this.isLoading = true
      try {
        const resp = await this.csvEditTemplateMarketPricesDB()
        if (!resp) throw new Error('No data')
        this.downloadCsvString(resp, 'PlantillaEdicionPrecios')
      } catch (e) {
        this.addTemporalNotification({
          type: 'danger',
          message: 'Error al descargar la plantilla'
        })
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },

    triggerBulkUpload () {
      this.$refs.bulkFileInput.click()
    },

    parseCsv (text) {
      const rows = []
      let row = []
      let field = ''
      let inQuotes = false
      const clean = text.replace(/^\uFEFF/, '')

      for (let i = 0; i < clean.length; i++) {
        const char = clean[i]

        if (inQuotes) {
          if (char === '"') {
            if (clean[i + 1] === '"') {
              field += '"'
              i++
            } else {
              inQuotes = false
            }
          } else {
            field += char
          }
        } else if (char === '"') {
          inQuotes = true
        } else if (char === ',') {
          row.push(field)
          field = ''
        } else if (char === '\n') {
          row.push(field)
          rows.push(row)
          row = []
          field = ''
        } else if (char !== '\r') {
          field += char
        }
      }
      if (field.length > 0 || row.length > 0) {
        row.push(field)
        rows.push(row)
      }
      return rows
    },

    onBulkFileSelected (event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return

      this.bulkFileName = file.name
      const reader = new FileReader()

      reader.onload = (e) => {
        try {
          this.buildBulkPreview(e.target.result)
          this.isBulkModalOpen = true
        } catch (err) {
          this.addTemporalNotification({
            type: 'danger',
            message: 'No se pudo leer el archivo. Verificá que sea la plantilla correcta.'
          })
          console.error(err)
        } finally {
          event.target.value = ''
        }
      }

      reader.readAsText(file)
    },

    buildBulkPreview (text) {
      const rows = this.parseCsv(text)
      if (!rows.length) throw new Error('Archivo vacío')

      const header = rows[0].map(h => h.trim().toLowerCase())
      const idIdx = header.indexOf('id')
      const newIdx = header.indexOf('nueva mediana')

      if (idIdx === -1 || newIdx === -1) {
        throw new Error('Faltan columnas "id" o "Nueva mediana"')
      }

      const byId = {}
      this.marketPrices.forEach(p => { byId[Number(p.id)] = p })

      const preview = []
      let notFound = 0
      let invalid = 0

      for (let i = 1; i < rows.length; i++) {
        const cells = rows[i]
        if (!cells || cells.length === 0) continue

        const rawNew = (cells[newIdx] || '').trim()
        if (!rawNew) continue

        const id = Number((cells[idIdx] || '').trim())
        const newBase = Number(rawNew.replace(/[^0-9]/g, ''))

        if (!id || isNaN(id) || isNaN(newBase) || newBase < 0) {
          invalid++
          continue
        }

        const product = byId[id]
        if (!product) {
          notFound++
          continue
        }

        const oldBase = Number(product.originalPrice) || 0
        preview.push({
          id,
          model: product.model,
          storage: product.storage,
          oldBase,
          newBase,
          oldGrades: {
            A: product.A,
            AB: product.AB,
            B: product.B,
            C: product.C
          },
          newGrades: {
            A: Math.round(this.marketBaseFront(newBase) * this.grades.A),
            AB: Math.round(this.marketBaseFront(newBase) * this.grades.AB),
            B: Math.round(this.marketBaseFront(newBase) * this.grades.B),
            C: Math.round(this.marketBaseFront(newBase) * this.grades.C)
          },
          changed: newBase !== oldBase
        })
      }

      this.bulkRows = preview
      this.bulkNotFoundCount = notFound
      this.bulkInvalidCount = invalid
    },

    closeBulkModal () {
      this.isBulkModalOpen = false
      this.bulkRows = []
      this.bulkFileName = ''
      this.bulkNotFoundCount = 0
      this.bulkInvalidCount = 0
    },

    async applyBulkChanges () {
      const updates = this.bulkRows
        .filter(r => r.changed)
        .map(r => ({ id: r.id, median: r.newBase }))

      if (!updates.length) return

      try {
        this.isApplyingBulk = true
        const resp = await this.bulkUpdateMarketPricesDB(updates)
        if (resp) {
          this.closeBulkModal()
          await this.getMarketPrices()
        }
      } catch (e) {
        this.addTemporalNotification({ type: 'danger', message: 'Error al aplicar los cambios' })
        console.error(e)
      } finally {
        this.isApplyingBulk = false
      }
    }
  }
}
</script>

<style scoped>
.section { width: 100%; padding: 0 20px; }

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-top: 12px;
}

.storage-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.storage-chip {
  border: 1px solid #dbdbdb;
  background: #fff;
  color: #555;
  border-radius: 999px;
  padding: 0.3em 0.85em;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s ease;
}

.storage-chip:hover {
  border-color: #00d1b2;
  color: #00947e;
}

.storage-chip.is-active {
  background: #00d1b2;
  border-color: #00d1b2;
  color: #fff;
  box-shadow: 0 1px 3px rgba(0, 209, 178, 0.4);
}

.results-count {
  margin-top: 8px;
  font-size: 13px;
  color: #666;
}

.results-count strong {
  color: #00947e;
}

.col-grade-partner {
  font-weight: 600;
  color: #3273dc;
  background-color: #f5f8ff;
}

.col-grade-consignment {
  background-color: #fff9ec;
}

.grade-consignment {
  background: #f9a825;
  color: #fff;
  box-shadow: 0 1px 3px rgba(249, 168, 37, 0.4);
}

.button-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.button-grid .button {
  width: 100%;
}

.input.has-text-centered {
  font-weight: 600;
  font-size: 1rem;
}

.columns.is-multiline {
  margin-bottom: 0.5rem;
}

.modal-card-body p {
  font-size: 1.1rem;
  color: #3273dc;
}

::v-deep td.col-model {
  font-weight: 600;
  color: #363636;
}

.brand-text {
  color: #7a7a7a;
  font-size: 13px;
}

.price-original {
  font-weight: 700;
  color: #363636;
}

.grade-pill {
  display: inline-block;
  min-width: 78px;
  padding: 0.2em 0.6em;
  border-radius: 999px;
  font-weight: 600;
  font-size: 12px;
  line-height: 1.4;
  white-space: nowrap;
}

.grade-a {
  background: #e8f8f0;
  color: #1f7a52;
}

.grade-ab {
  background: #00d1b2;
  color: #fff;
  box-shadow: 0 1px 3px rgba(0, 209, 178, 0.4);
}

.grade-b {
  background: #fff5e0;
  color: #946c00;
}

.grade-c {
  background: #fdeaea;
  color: #a8322d;
}

.cond-pill {
  display: inline-block;
  padding: 0.2em 0.7em;
  border-radius: 999px;
  font-weight: 600;
  font-size: 11px;
  line-height: 1.4;
  white-space: nowrap;
}

.cond-reac {
  background: #eef2ff;
  color: #4053b8;
}

.cond-nuevo {
  background: #eef7f2;
  color: #1f7a52;
}

::v-deep tr.row-changed td {
  background: #fffbe6 !important;
}

.old-val {
  color: #b5b5b5;
  text-decoration: line-through;
  font-size: 12px;
}

.arrow {
  margin: 0 4px;
  color: #999;
}

.new-val {
  font-weight: 700;
  color: #00947e;
}
</style>
